import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  FaCalendarAlt,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaEye,
  FaFilter,
  FaGlobeAmericas,
  FaInfoCircle,
  FaPlus,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import RichTextEditor from "../../components/cms/RichTextEditor";
import ContactCmsEditor from "../../components/cms/ContactCmsEditor";
import AboutCmsEditor from "../../components/cms/AboutCmsEditor";
import PrivacyCmsEditor from "../../components/cms/PrivacyCmsEditor";
import RefundCmsEditor from "../../components/cms/RefundCmsEditor";
import DomainCmsEditor from "../../components/cms/DomainCmsEditor";
import CmsPreviewModal from "../../components/cms/CmsPreviewModal";
import CmsPageVersionOverview from "../../components/cms/CmsPageVersionOverview";
import CmsPageVersionHistoryTimeline from "../../components/cms/CmsPageVersionHistoryTimeline";
import { CMS_PAGE_TYPE_LABELS, CMS_PAGE_ROUTES, CMS_STATUS_OPTIONS } from "../../constants/cms";
import { CMS_SLUG_TO_PAGE_TYPE } from "../../config/superAdminCmsNavigation";
import {
  createCmsPageNewVersion,
  fetchCmsPageHistory,
  fetchCmsPages,
  publishCmsPage,
  unpublishCmsPage,
  updateCmsPage,
  cmsExtractMessage,
} from "../../services/cms.service";
import {
  contactConfigFromCmsRecord,
  contactCmsPayloadFromConfig,
  DEFAULT_CONTACT_PAGE_CONFIG,
} from "../../utils/contactPageCms";
import {
  aboutConfigFromCmsRecord,
  aboutCmsPayloadFromConfig,
  DEFAULT_ABOUT_CMS_CONFIG,
} from "../../utils/aboutPageCms";
import {
  privacyConfigFromCmsRecord,
  privacyCmsPayloadFromConfig,
  DEFAULT_PRIVACY_CMS_CONFIG,
} from "../../utils/privacyPageCms";
import {
  refundConfigFromCmsRecord,
  refundCmsPayloadFromConfig,
  DEFAULT_REFUND_CMS_CONFIG,
} from "../../utils/refundPageCms";
import {
  domainConfigFromCmsRecord,
  domainCmsPayloadFromConfig,
  DEFAULT_DOMAIN_CMS_CONFIG,
} from "../../utils/domainPageCms";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/cms_management.scss";
import "../../assets/styles/cms_page_editor.scss";
import "../../assets/styles/cms_contact_editor.scss";

const TITLE_MAX = 100;
const DESCRIPTION_MAX = 200;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

function CmsPageVersionRowActions({ item, saving, onView, onPreview, onPublish, onUnpublish }) {
  return (
    <div className="cms-page-table__actions">
      <button
        type="button"
        className="cms-page-action-btn"
        title="View in editor"
        aria-label={`View version ${item.version} in editor`}
        disabled={saving}
        onClick={() => onView(item)}
      >
        <FaEdit aria-hidden />
        <span>View</span>
      </button>
      <button
        type="button"
        className="cms-page-action-btn"
        title="Preview"
        aria-label={`Preview version ${item.version}`}
        disabled={saving}
        onClick={() => onPreview(item)}
      >
        <FaEye aria-hidden />
        <span>Preview</span>
      </button>
      {item.status === "DRAFT" ? (
        <button
          type="button"
          className="cms-page-action-btn cms-page-action-btn--success"
          title="Publish"
          aria-label={`Publish version ${item.version}`}
          disabled={saving}
          onClick={() => onPublish(item)}
        >
          <FaCheck aria-hidden />
          <span>Publish</span>
        </button>
      ) : null}
      {item.status === "ACTIVE" ? (
        <button
          type="button"
          className="cms-page-action-btn cms-page-action-btn--danger"
          title="Unpublish"
          aria-label={`Unpublish version ${item.version}`}
          disabled={saving}
          onClick={() => onUnpublish(item)}
        >
          <FaTimes aria-hidden />
          <span>Unpublish</span>
        </button>
      ) : null}
    </div>
  );
}

export default function SuperAdminCmsPageEditorPage() {
  const { pageSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageType = CMS_SLUG_TO_PAGE_TYPE[pageSlug];
  const isContactPage = pageType === "CONTACT_US";
  const isAboutPage = pageType === "ABOUT_US";
  const isPrivacyPage = pageType === "PRIVACY_POLICY";
  const isRefundPage = pageType === "REFUND_POLICY";
  const isDomainPage = pageType === "DOMAIN_REDIRECTION_POLICY";
  /** Marketing/legal pages: single live page — edit & save, no version workflow. */
  const isSimplePage =
    isAboutPage || isContactPage || isPrivacyPage || isRefundPage || isDomainPage;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState({
    title: "",
    subtitle: "",
    contentTitle: "",
    contentIntro: "",
    content: "",
    effectiveDate: "",
    updatedAt: "",
    updatedBy: "",
    status: "",
  });
  const workspaceRef = useRef(null);
  const simpleBootstrapRef = useRef(false);
  const [listFilters, setListFilters] = useState({ status: "", search: "", page: 1, pageSize: 10 });
  const [listResult, setListResult] = useState({ items: [], total: 0, totalPages: 1 });
  const [form, setForm] = useState({
    title: "",
    description: "",
    content: "",
    effective_date: dayjs().format("YYYY-MM-DD"),
  });
  const [contactConfig, setContactConfig] = useState(DEFAULT_CONTACT_PAGE_CONFIG);
  const [aboutConfig, setAboutConfig] = useState(DEFAULT_ABOUT_CMS_CONFIG);
  const [privacyConfig, setPrivacyConfig] = useState(DEFAULT_PRIVACY_CMS_CONFIG);
  const [refundConfig, setRefundConfig] = useState(DEFAULT_REFUND_CMS_CONFIG);
  const [domainConfig, setDomainConfig] = useState(DEFAULT_DOMAIN_CMS_CONFIG);

  const isReadOnly = isSimplePage ? false : selected?.status !== "DRAFT";
  const pageLabel = CMS_PAGE_TYPE_LABELS[pageType] || pageType;

  const applyCmsRecordToForm = useCallback(
    (record) => {
      if (!record) {
        setSelected(null);
        setForm({
          title: "",
          description: "",
          content: "",
          effective_date: dayjs().format("YYYY-MM-DD"),
        });
        setContactConfig(DEFAULT_CONTACT_PAGE_CONFIG);
        setAboutConfig(DEFAULT_ABOUT_CMS_CONFIG);
        setPrivacyConfig(DEFAULT_PRIVACY_CMS_CONFIG);
        setRefundConfig(DEFAULT_REFUND_CMS_CONFIG);
        setDomainConfig(DEFAULT_DOMAIN_CMS_CONFIG);
        return;
      }

      setSelected(record);
      if (isContactPage) {
        const config = contactConfigFromCmsRecord(record);
        const payload = contactCmsPayloadFromConfig(config);
        setContactConfig(config);
        setForm({
          title: payload.title,
          description: payload.description,
          content: payload.content,
          effective_date: record.effectiveDate || dayjs().format("YYYY-MM-DD"),
        });
      } else if (isAboutPage) {
        const config = aboutConfigFromCmsRecord(record);
        const payload = aboutCmsPayloadFromConfig(config);
        setAboutConfig(config);
        setForm({
          title: payload.title,
          description: payload.description,
          content: payload.content,
          effective_date: record.effectiveDate || dayjs().format("YYYY-MM-DD"),
        });
      } else if (isPrivacyPage) {
        const config = privacyConfigFromCmsRecord(record);
        const payload = privacyCmsPayloadFromConfig(config);
        setPrivacyConfig(config);
        setForm({
          title: payload.title,
          description: payload.description,
          content: payload.content,
          effective_date: record.effectiveDate || dayjs().format("YYYY-MM-DD"),
        });
      } else if (isRefundPage) {
        const config = refundConfigFromCmsRecord(record);
        const payload = refundCmsPayloadFromConfig(config);
        setRefundConfig(config);
        setForm({
          title: payload.title,
          description: payload.description,
          content: payload.content,
          effective_date: record.effectiveDate || dayjs().format("YYYY-MM-DD"),
        });
      } else if (isDomainPage) {
        const config = domainConfigFromCmsRecord(record);
        const payload = domainCmsPayloadFromConfig(config);
        setDomainConfig(config);
        setForm({
          title: payload.title,
          description: payload.description,
          content: payload.content,
          effective_date: record.effectiveDate || dayjs().format("YYYY-MM-DD"),
        });
      } else {
        setForm({
          title: record.title || "",
          description: record.description || "",
          content: record.content || "",
          effective_date: record.effectiveDate || dayjs().format("YYYY-MM-DD"),
        });
      }
    },
    [isContactPage, isAboutPage, isPrivacyPage, isRefundPage, isDomainPage],
  );

  const buildSavePayload = useCallback(() => {
    if (isContactPage) {
      const payload = contactCmsPayloadFromConfig(contactConfig);
      return {
        title: payload.title,
        description: payload.description,
        content: payload.content,
        effective_date: form.effective_date,
      };
    }
    if (isAboutPage) {
      const payload = aboutCmsPayloadFromConfig(aboutConfig);
      return {
        title: payload.title,
        description: payload.description,
        content: payload.content,
        effective_date: form.effective_date,
      };
    }
    if (isPrivacyPage) {
      const payload = privacyCmsPayloadFromConfig(privacyConfig);
      return {
        title: payload.title,
        description: payload.description,
        content: payload.content,
        effective_date: form.effective_date,
      };
    }
    if (isRefundPage) {
      const payload = refundCmsPayloadFromConfig(refundConfig);
      return {
        title: payload.title,
        description: payload.description,
        content: payload.content,
        effective_date: form.effective_date,
      };
    }
    if (isDomainPage) {
      const payload = domainCmsPayloadFromConfig(domainConfig);
      return {
        title: payload.title,
        description: payload.description,
        content: payload.content,
        effective_date: form.effective_date,
      };
    }
    return {
      title: form.title.slice(0, TITLE_MAX),
      description: form.description.slice(0, DESCRIPTION_MAX),
      content: form.content,
      effective_date: form.effective_date,
    };
  }, [isContactPage, isAboutPage, isPrivacyPage, isRefundPage, isDomainPage, contactConfig, aboutConfig, privacyConfig, refundConfig, domainConfig, form]);

  const loadModuleData = useCallback(async () => {
    if (!pageType) return;
    setLoading(true);
    try {
      const [historyRows, listData] = await Promise.all([
        fetchCmsPageHistory(pageType),
        fetchCmsPages({
          page_type: pageType,
          status: listFilters.status || undefined,
          search: listFilters.search?.trim() || undefined,
          page: listFilters.page,
          page_size: listFilters.pageSize,
        }),
      ]);
      setHistory(historyRows);
      setListResult(listData);

      if (isSimplePage) {
        const active =
          historyRows.find((r) => r.status === "ACTIVE") ||
          historyRows.find((r) => r.status === "DRAFT") ||
          historyRows[0];

        if (active) {
          applyCmsRecordToForm(active);
          simpleBootstrapRef.current = false;
        } else if (!simpleBootstrapRef.current) {
          simpleBootstrapRef.current = true;
          try {
            const created = await createCmsPageNewVersion(pageType);
            const payload = isAboutPage
              ? aboutCmsPayloadFromConfig(DEFAULT_ABOUT_CMS_CONFIG)
              : isPrivacyPage
                ? privacyCmsPayloadFromConfig(DEFAULT_PRIVACY_CMS_CONFIG)
                : isRefundPage
                  ? refundCmsPayloadFromConfig(DEFAULT_REFUND_CMS_CONFIG)
                  : isDomainPage
                    ? domainCmsPayloadFromConfig(DEFAULT_DOMAIN_CMS_CONFIG)
                  : contactCmsPayloadFromConfig(DEFAULT_CONTACT_PAGE_CONFIG);
            const published = await updateCmsPage(created.id, {
              title: payload.title,
              description: payload.description,
              content: payload.content,
              effective_date: dayjs().format("YYYY-MM-DD"),
              publish: true,
            });
            applyCmsRecordToForm(published);
            const refreshedHistory = await fetchCmsPageHistory(pageType);
            setHistory(refreshedHistory);
            setListResult({
              items: refreshedHistory,
              total: refreshedHistory.length,
              totalPages: 1,
            });
          } catch (bootErr) {
            simpleBootstrapRef.current = false;
            applyCmsRecordToForm(null);
            toast.error(cmsExtractMessage(bootErr, "Failed to initialize page"));
          }
        } else {
          applyCmsRecordToForm(null);
        }
      } else {
        const versionId = Number(searchParams.get("version"));
        const initial = versionId
          ? historyRows.find((r) => r.id === versionId)
          : historyRows.find((r) => r.status === "DRAFT") ||
            historyRows.find((r) => r.status === "ACTIVE");

        if (initial) {
          applyCmsRecordToForm(initial);
        } else {
          applyCmsRecordToForm(null);
        }
      }
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to load page"));
    } finally {
      setLoading(false);
    }
  }, [pageType, listFilters, searchParams, applyCmsRecordToForm, isSimplePage, isAboutPage, isPrivacyPage, isRefundPage, isDomainPage]);

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);

  const handleSelectVersion = useCallback(
    (item) => {
      applyCmsRecordToForm(item);
      setSearchParams({ version: String(item.id) });
    },
    [applyCmsRecordToForm, setSearchParams],
  );

  const handleSaveDraft = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      const payload = buildSavePayload();
      const updated = await updateCmsPage(selected.id, {
        ...payload,
        publish: false,
      });
      toast.success("Draft saved");
      setSelected(updated);
      await loadModuleData();
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to save draft"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSimplePage = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      const payload = buildSavePayload();
      const updated = await updateCmsPage(selected.id, {
        ...payload,
        publish: true,
      });
      toast.success("Page saved");
      applyCmsRecordToForm(updated);
      await loadModuleData();
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to save page"));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      if (selected.status === "DRAFT") {
        const payload = buildSavePayload();
        await updateCmsPage(selected.id, {
          ...payload,
          publish: true,
        });
      } else {
        await publishCmsPage(selected.id);
      }
      toast.success("Page published");
      await loadModuleData();
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to publish"));
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      await unpublishCmsPage(selected.id);
      toast.success("Page unpublished");
      await loadModuleData();
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to unpublish"));
    } finally {
      setSaving(false);
    }
  };

  const handleNewVersion = async () => {
    setSaving(true);
    try {
      const created = await createCmsPageNewVersion(pageType);
      toast.success(`Version v${created.version} created`);
      setSearchParams({ version: String(created.id) });
      await loadModuleData();
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to create new version"));
    } finally {
      setSaving(false);
    }
  };

  const openPreview = useCallback(
    (item) => {
      const source = item || selected;

      if (isContactPage || isAboutPage || isPrivacyPage || isRefundPage || isDomainPage) {
        window.open(
          isAboutPage
            ? "/about-us"
            : isPrivacyPage
              ? "/privacy-policy"
              : isRefundPage
                ? "/refund-cancellation-policy"
                : isDomainPage
                  ? "/domain-redirection-policy"
                : "/contact",
          "_blank",
          "noopener,noreferrer",
        );
        return;
      }

      const isCurrentDraft =
        source?.status === "DRAFT" && source?.id && source.id === selected?.id;
      const pageTitle = isCurrentDraft ? form.title : source?.title || form.title;
      const pageDescription = isCurrentDraft ? form.description : source?.description || form.description;
      const content = isCurrentDraft
        ? form.content || source?.content || ""
        : source?.content || form.content || "";

      if (!pageTitle?.trim() && !content?.trim()) {
        toast.info("Nothing to preview yet.");
        return;
      }

      setPreviewModal({
        title: `${pageLabel} (v${source?.version || 1})`,
        subtitle:
          source?.status === "DRAFT"
            ? "Review this draft before publishing."
            : "Review the active page before publishing.",
        contentTitle: pageTitle || pageLabel,
        contentIntro: pageDescription || "",
        content,
        effectiveDate: isCurrentDraft ? form.effective_date : source?.effectiveDate,
        updatedAt: source?.updatedAt,
        updatedBy: source?.updatedBy || source?.createdBy || "Super Admin",
        status: source?.status || "DRAFT",
      });
      setPreviewOpen(true);
    },
    [
      selected,
      form.title,
      form.description,
      form.content,
      form.effective_date,
      pageLabel,
      isContactPage,
      isAboutPage,
      isPrivacyPage,
      isRefundPage,
      isDomainPage,
    ],
  );

  const handleViewVersion = useCallback(
    (item) => {
      handleSelectVersion(item);
      workspaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [handleSelectVersion],
  );

  const handlePublishVersion = async (item) => {
    if (!item?.id) return;
    setSaving(true);
    try {
      if (item.status === "DRAFT" && item.id === selected?.id) {
        const payload = buildSavePayload();
        await updateCmsPage(item.id, {
          ...payload,
          publish: true,
        });
      } else if (item.status === "DRAFT") {
        await updateCmsPage(item.id, {
          title: item.title,
          description: item.description,
          content: item.content,
          effective_date: item.effectiveDate,
          publish: true,
        });
      } else {
        await publishCmsPage(item.id);
      }
      toast.success(`Version v${item.version} published`);
      await loadModuleData();
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to publish"));
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublishVersion = async (item) => {
    if (!item?.id) return;
    setSaving(true);
    try {
      await unpublishCmsPage(item.id);
      toast.success(`Version v${item.version} unpublished`);
      await loadModuleData();
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to unpublish"));
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = useMemo(() => CMS_PAGE_ROUTES[pageType], [pageType]);
  const totalPages = listResult.totalPages || 1;
  const tableRows = listResult.items?.length ? listResult.items : history;

  if (!pageType) {
    return (
      <div className="sa-page">
        <p>Unknown page type.</p>
        <Link to="/super-admin/cms">Back to CMS Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="cms-page-editor">
      <header className="cms-page-editor__header">
        <div>
          <nav className="cms-page-editor__breadcrumb" aria-label="Breadcrumb">
            <Link to="/super-admin/cms">CMS Management</Link>
            <span aria-hidden>&gt;</span>
            <span>{pageLabel}</span>
          </nav>
          <div className="cms-page-editor__title-row">
            <h1 className="cms-page-editor__title">{pageLabel}</h1>
            {selected?.status ? (
              <span className={`cms-page-status cms-page-status--${selected.status.toLowerCase()}`}>
                {selected.status}
              </span>
            ) : null}
            {!isSimplePage && selected ? (
              <span className="cms-page-editor__version-label">Version v{selected.version}</span>
            ) : null}
          </div>
        </div>
        <div className="cms-page-editor__header-actions">
          {publicUrl ? (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cms-page-btn cms-page-btn--outline"
            >
              <FaGlobeAmericas aria-hidden />
              Public Page
            </a>
          ) : null}
          {!isSimplePage ? (
            <button type="button" className="cms-page-btn cms-page-btn--outline" onClick={() => openPreview(selected)}>
              <FaEye aria-hidden />
              Preview
            </button>
          ) : null}
          {!isSimplePage ? (
            <button
              type="button"
              className="cms-page-btn cms-page-btn--primary"
              onClick={handleNewVersion}
              disabled={saving}
            >
              <FaPlus aria-hidden />
              New Version
            </button>
          ) : selected ? (
            <button
              type="button"
              className="cms-page-btn cms-page-btn--primary"
              onClick={handleSaveSimplePage}
              disabled={saving}
            >
              Save
            </button>
          ) : null}
        </div>
      </header>

      {!isSimplePage ? (
      <div className="cms-page-toolbar">
        <label className="cms-page-toolbar__search">
          <FaSearch aria-hidden />
          <input
            type="search"
            placeholder="Search title or content..."
            value={listFilters.search}
            onChange={(e) => setListFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
          />
        </label>
        <label className="cms-page-toolbar__filter">
          <FaFilter aria-hidden />
          <select
            value={listFilters.status}
            onChange={(e) => setListFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
          >
            {CMS_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      ) : null}

      <div
        className={`cms-page-editor__workspace${isSimplePage ? " cms-page-editor__workspace--simple" : ""}`}
        ref={workspaceRef}
      >
        <section className="cms-page-editor__main">
          <div className="cms-page-editor-card">
            {loading ? (
              <p className="cms-page-editor-card__loading">Loading...</p>
            ) : !selected ? (
              <div className="cms-page-editor-card__empty">
                <p>
                  {isSimplePage
                    ? "Setting up this page..."
                    : "No page version found. Create a new version to get started."}
                </p>
                {!isSimplePage ? (
                  <button
                    type="button"
                    className="cms-page-btn cms-page-btn--primary"
                    onClick={handleNewVersion}
                    disabled={saving}
                  >
                    <FaPlus aria-hidden />
                    New Version
                  </button>
                ) : null}
              </div>
            ) : (
              <>
                {isContactPage ? (
                  <ContactCmsEditor
                    config={contactConfig}
                    onChange={setContactConfig}
                    readOnly={isReadOnly}
                  />
                ) : isAboutPage ? (
                  <AboutCmsEditor
                    config={aboutConfig}
                    onChange={setAboutConfig}
                    readOnly={isReadOnly}
                  />
                ) : isPrivacyPage ? (
                  <PrivacyCmsEditor
                    config={privacyConfig}
                    onChange={setPrivacyConfig}
                    readOnly={isReadOnly}
                    effectiveDate={form.effective_date}
                    onEffectiveDateChange={(value) =>
                      setForm((f) => ({ ...f, effective_date: value }))
                    }
                  />
                ) : isRefundPage ? (
                  <RefundCmsEditor
                    config={refundConfig}
                    onChange={setRefundConfig}
                    readOnly={isReadOnly}
                    effectiveDate={form.effective_date}
                    onEffectiveDateChange={(value) =>
                      setForm((f) => ({ ...f, effective_date: value }))
                    }
                  />
                ) : isDomainPage ? (
                  <DomainCmsEditor
                    config={domainConfig}
                    onChange={setDomainConfig}
                    readOnly={isReadOnly}
                    effectiveDate={form.effective_date}
                    onEffectiveDateChange={(value) =>
                      setForm((f) => ({ ...f, effective_date: value }))
                    }
                  />
                ) : (
                  <>
                <div className="cms-page-field">
                  <label htmlFor="cms-title">
                    <span className="cms-page-field__label-text">
                      Title
                      <span className="cms-page-field__required" aria-hidden>
                        *
                      </span>
                    </span>
                    <span className="cms-page-field__counter">
                      {form.title.length} / {TITLE_MAX}
                    </span>
                  </label>
                  <input
                    id="cms-title"
                    type="text"
                    className="cms-page-field__input"
                    maxLength={TITLE_MAX}
                    value={form.title}
                    disabled={isReadOnly}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  />
                </div>

                <div className="cms-page-field">
                  <label htmlFor="cms-description">
                    <span className="cms-page-field__label-text">
                      Description
                      <span className="cms-page-field__required" aria-hidden>
                        *
                      </span>
                    </span>
                    <span className="cms-page-field__counter">
                      {form.description.length} / {DESCRIPTION_MAX}
                    </span>
                  </label>
                  <textarea
                    id="cms-description"
                    rows={2}
                    className="cms-page-field__textarea"
                    maxLength={DESCRIPTION_MAX}
                    value={form.description}
                    disabled={isReadOnly}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>

                <div className="cms-page-field">
                  <label>
                    <span className="cms-page-field__label-text">
                      Content
                      <span className="cms-page-field__required" aria-hidden>
                        *
                      </span>
                    </span>
                  </label>
                  <div className="cms-page-field__editor">
                    <RichTextEditor
                      value={form.content}
                      onChange={(value) => setForm((f) => ({ ...f, content: value }))}
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>
                  </>
                )}

                {!isSimplePage ? (
                <div className="cms-page-field">
                  <label htmlFor="cms-effective-date">
                    <span className="cms-page-field__label-text">Effective Date</span>
                  </label>
                  <div className="cms-page-field__date-wrap">
                    <FaCalendarAlt className="cms-page-field__date-icon" aria-hidden />
                    <input
                      id="cms-effective-date"
                      type="date"
                      value={form.effective_date}
                      disabled={isReadOnly}
                      onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                    />
                  </div>
                </div>
                ) : null}

                <div className="cms-page-editor-card__footer">
                  {isSimplePage ? (
                    <div className="cms-page-editor-card__actions">
                      <button
                        type="button"
                        className="cms-page-btn cms-page-btn--primary"
                        onClick={handleSaveSimplePage}
                        disabled={saving}
                      >
                        Save
                      </button>
                    </div>
                  ) : isReadOnly ? (
                    <div className="cms-page-readonly-row">
                      <div className="cms-page-readonly-banner">
                        <FaInfoCircle aria-hidden />
                        <span>This version is read-only. Create a new version to make changes.</span>
                      </div>
                      {selected?.status === "ACTIVE" ? (
                        <button
                          type="button"
                          className="cms-page-btn cms-page-btn--danger-outline cms-page-btn--compact"
                          onClick={handleUnpublish}
                          disabled={saving}
                        >
                          Unpublish
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="cms-page-editor-card__actions">
                      <button
                        type="button"
                        className="cms-page-btn cms-page-btn--outline"
                        onClick={handleSaveDraft}
                        disabled={saving}
                      >
                        Save Draft
                      </button>
                      <button
                        type="button"
                        className="cms-page-btn cms-page-btn--primary"
                        onClick={handlePublish}
                        disabled={saving}
                      >
                        Publish
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        {!isSimplePage ? (
        <aside className="cms-page-editor__sidebar">
          <CmsPageVersionOverview version={selected} />
          <CmsPageVersionHistoryTimeline
            history={history}
            selectedId={selected?.id}
            onSelect={handleSelectVersion}
          />
        </aside>
        ) : null}
      </div>

      {!isSimplePage ? (
      <section className="cms-page-table-section">
        <h3 className="cms-page-table-section__title">
          All Versions ({listResult.total || history.length})
        </h3>
        <div className="cms-page-table-wrap">
          <table className="cms-page-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Status</th>
                <th>Title</th>
                <th>Effective Date</th>
                <th>Updated By</th>
                <th>Updated On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="cms-page-table__empty">
                    Loading...
                  </td>
                </tr>
              ) : tableRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="cms-page-table__empty">
                    No versions found.
                  </td>
                </tr>
              ) : (
                tableRows.map((item) => (
                  <tr key={item.id}>
                    <td>v{item.version}</td>
                    <td>
                      <span className={`cms-page-status cms-page-status--${item.status.toLowerCase()}`}>
                        {item.status}
                      </span>
                    </td>
                    <td>{item.title || "—"}</td>
                    <td>{item.effectiveDate || "—"}</td>
                    <td title={item.updatedBy || ""}>{item.updatedBy || "—"}</td>
                    <td>{item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "—"}</td>
                    <td className="cms-page-table__actions-cell">
                      <CmsPageVersionRowActions
                        item={item}
                        saving={saving}
                        onView={handleViewVersion}
                        onPreview={openPreview}
                        onPublish={handlePublishVersion}
                        onUnpublish={handleUnpublishVersion}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="cms-page-table-footer">
          <label className="cms-page-table-footer__page-size">
            Rows per page:
            <select
              value={listFilters.pageSize}
              onChange={(e) =>
                setListFilters((f) => ({ ...f, pageSize: Number(e.target.value), page: 1 }))
              }
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <div className="cms-page-table-footer__pager">
            <button
              type="button"
              className="cms-page-icon-btn"
              disabled={listFilters.page <= 1}
              onClick={() => setListFilters((f) => ({ ...f, page: f.page - 1 }))}
              aria-label="Previous page"
            >
              <FaChevronLeft aria-hidden />
            </button>
            <span className="cms-page-table-footer__page-num">{listFilters.page}</span>
            <button
              type="button"
              className="cms-page-icon-btn"
              disabled={listFilters.page >= totalPages}
              onClick={() => setListFilters((f) => ({ ...f, page: f.page + 1 }))}
              aria-label="Next page"
            >
              <FaChevronRight aria-hidden />
            </button>
          </div>
        </div>
      </section>
      ) : null}

      <CmsPreviewModal
        open={previewOpen}
        variant="page"
        title={previewModal.title || `${pageLabel} (v1)`}
        subtitle={previewModal.subtitle}
        contentTitle={previewModal.contentTitle}
        contentIntro={previewModal.contentIntro}
        content={previewModal.content}
        effectiveDate={previewModal.effectiveDate}
        updatedAt={previewModal.updatedAt}
        updatedBy={previewModal.updatedBy}
        status={previewModal.status}
        publicUrl={publicUrl}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
}
